import React, { createContext, useContext, useState, useEffect } from 'react';
import { VoterProfile, AdminUser } from '../types';

interface AuthContextType {
  voter: VoterProfile | null;
  voterToken: string | null;
  admin: AdminUser | null;
  adminToken: string | null;
  activeElectionId: string;
  loginVoter: (profile: VoterProfile, token: string) => void;
  logoutVoter: () => void;
  loginAdmin: (user: AdminUser, token: string) => void;
  logoutAdmin: () => void;
  setActiveElectionId: (id: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [voter, setVoter] = useState<VoterProfile | null>(() => {
    const saved = localStorage.getItem('electpoll_voter');
    return saved ? JSON.parse(saved) : null;
  });

  const [voterToken, setVoterToken] = useState<string | null>(() => {
    return localStorage.getItem('electpoll_voter_token');
  });

  const [admin, setAdmin] = useState<AdminUser | null>(() => {
    const saved = localStorage.getItem('electpoll_admin');
    return saved ? JSON.parse(saved) : null;
  });

  const [adminToken, setAdminToken] = useState<string | null>(() => {
    return localStorage.getItem('electpoll_admin_token');
  });

  const [activeElectionId, setActiveElectionId] = useState<string>(() => {
    return localStorage.getItem('electpoll_active_election') || 'lss-ui-2025';
  });

  const loginVoter = (profile: VoterProfile, token: string) => {
    setVoter(profile);
    setVoterToken(token);
    localStorage.setItem('electpoll_voter', JSON.stringify(profile));
    localStorage.setItem('electpoll_voter_token', token);
    localStorage.setItem('userName', profile.name);
    localStorage.setItem('userEmail', profile.email);
    localStorage.setItem('userMatric', profile.matric);
    localStorage.setItem('userLevel', profile.level);
    localStorage.setItem('userDepartment', profile.department || 'Law');
  };

  const logoutVoter = () => {
    setVoter(null);
    setVoterToken(null);
    localStorage.removeItem('electpoll_voter');
    localStorage.removeItem('electpoll_voter_token');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userMatric');
    localStorage.removeItem('userLevel');
  };

  const loginAdmin = (user: AdminUser, token: string) => {
    setAdmin(user);
    setAdminToken(token);
    localStorage.setItem('electpoll_admin', JSON.stringify(user));
    localStorage.setItem('electpoll_admin_token', token);
  };

  const logoutAdmin = () => {
    setAdmin(null);
    setAdminToken(null);
    localStorage.removeItem('electpoll_admin');
    localStorage.removeItem('electpoll_admin_token');
  };

  const handleSetActiveElection = (id: string) => {
    setActiveElectionId(id);
    localStorage.setItem('electpoll_active_election', id);
  };

  return (
    <AuthContext.Provider
      value={{
        voter,
        voterToken,
        admin,
        adminToken,
        activeElectionId,
        loginVoter,
        logoutVoter,
        loginAdmin,
        logoutAdmin,
        setActiveElectionId: handleSetActiveElection,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
